import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, DollarSign, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const lessons = [
  {
    icon: DollarSign,
    title: "Carrying salaries we could not afford",
    description: "We learned what it means to have people depending on you, even when revenue does not show up.",
  },
  {
    icon: Clock,
    title: "Building for months before asking anyone to pay",
    description: "We spent time and money on features nobody wanted. Validation came too late.",
  },
  {
    icon: AlertTriangle,
    title: "Scaling before we had clarity",
    description: "Growth felt good until we realized we were scaling something that did not have product-market fit.",
  },
  {
    icon: Heart,
    title: "Personal sacrifice without perspective",
    description: "Health, relationships, savings, everything on the line for something we were not sure was real.",
  },
];

// Person reading/thinking illustration
const illustration = "/images/why-we-exist.png";

export default function WhyWeExistPage() {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="bg-hero py-16 sm:py-20 lg:py-24">
        <div className="container-narrow">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center"
          >
            <h1 className="heading-display text-foreground mb-6">
              Why We Exist
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              Because we have been there. We have felt the weight of founder failure, and we have learned what it costs when clarity comes too late.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story with Illustration */}
      <section className="bg-background py-16 sm:py-20 lg:py-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className="flex justify-center"
            >
              <div className="w-full max-w-sm">
                <img
                  src={illustration}
                  alt="Founder reading and thinking"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="space-y-6"
            >
              <p className="body-large text-muted-foreground">
                Let's Build Startup was not born from a business plan. It was born from lived experience, from the kind of lessons you only learn when you are deep in it.
              </p>
              
              <div className="quote-block">
                <p>
                  We have carried salaries when revenue did not show up. We have watched ideas we believed in fail because we moved too fast, or too slow, or in the wrong direction entirely.
                </p>
              </div>

              <p className="body-base text-muted-foreground">
                Most founders do not fail because their ideas are bad. They fail because they get clarity too late, after the money is spent, after the team is burned out, after the opportunity has passed.
              </p>

              <p className="body-base text-muted-foreground">
                We exist because we want to give other founders what we did not have: a partner who understands the stakes, who can help cut through uncertainty, and who cares about outcomes, not just deliverables.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lessons */}
      <section className="bg-section py-16 sm:py-20 lg:py-24">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-foreground mb-5">What We Learned the Hard Way</h2>
            <p className="body-base text-muted-foreground">
              These are not theories. They are scars.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {lessons.map((lesson, i) => (
              <motion.div
                key={lesson.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="card-flat"
              >
                <div className="flex gap-4">
                  <div className="icon-container flex-shrink-0">
                    <lesson.icon className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-medium text-foreground mb-2">{lesson.title}</h3>
                    <p className="text-muted-foreground">{lesson.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="bg-background py-16 sm:py-20 lg:py-24">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section text-foreground mb-8 text-center">Our Founding Philosophy</h2>
            
            <div className="bg-card border border-border/50 rounded-xl p-8 space-y-5">
              <div className="flex items-start gap-4">
                <span className="text-accent font-serif text-xl font-medium">1.</span>
                <p className="text-foreground"><strong>Early validation over blind execution.</strong> Know if it is real before you commit everything.</p>
              </div>
              <Separator />
              <div className="flex items-start gap-4">
                <span className="text-accent font-serif text-xl font-medium">2.</span>
                <p className="text-foreground"><strong>Clarity before commitment.</strong> Do not scale confusion.</p>
              </div>
              <Separator />
              <div className="flex items-start gap-4">
                <span className="text-accent font-serif text-xl font-medium">3.</span>
                <p className="text-foreground"><strong>Learning before scaling.</strong> Growth without understanding is just expensive chaos.</p>
              </div>
              <Separator />
              <div className="flex items-start gap-4">
                <span className="text-accent font-serif text-xl font-medium">4.</span>
                <p className="text-foreground"><strong>Small teams before big burn.</strong> Stay lean until you know what works.</p>
              </div>
              <Separator />
              <div className="flex items-start gap-4">
                <span className="text-accent font-serif text-xl font-medium">5.</span>
                <p className="text-foreground"><strong>Reality over assumptions.</strong> What customers actually do matters more than what you hope they will do.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground py-16 sm:py-20">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section mb-5">Want to See How We Work?</h2>
            <p className="body-base text-primary-foreground/80 mb-6">
              Learn more about our process and how we help founders find clarity.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
                <Link to="/process">
                  Read Our Process
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
                <Link to="/founder">Read the Founder Story</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
